<script setup lang="ts" generic="T extends FormChoiceValue">
// Composables
import useFormControl from "../composables/useFormControl";

// Types
import type { FormChoiceEntry, FormChoiceValue } from "../types/formselect";

const { options } = defineProps<{
  options: FormChoiceEntry<T>[];
  placeholder?: string;
}>();

const field = useFormControl();

if (!field)
  throw new Error(
    "[FormSelect]: field instance not available. Did you wrap this component inside a FormControl?",
  );

const _name = unref(field.name);

const _inputModel = defineModel<FormChoiceValue | undefined>({
  set(value) {
    field.setValue(value || undefined, field.validateOnValueUpdate);

    return value;
  },
});

if (field.value.value) {
  _inputModel.value =
    typeof field.value.value !== "object"
      ? (String(field.value.value || "") as FormChoiceValue)
      : (field.value.value as unknown as FormChoiceValue);
}

defineExpose({
  field,
});
</script>

<template>
  <select
    :id="field?.inputId || _name"
    :class="[
      'form-select',
      { 'form-select--placeholder': placeholder && !_inputModel },
    ]"
    :name="_name"
    v-model="_inputModel"
  >
    <option v-if="placeholder" disabled :value="undefined">
      {{ placeholder }}
    </option>
    <option
      v-for="(option, key) in options"
      :key="key"
      :value="option.value"
      :disabled="option.disabled"
    >
      {{ option.label }}
    </option>
  </select>
</template>

<style scoped lang="scss">
.form-select {
  width: 100%;
  height: var(--form-input-height, 60px);
  overflow: hidden;
  color: var(--cm-color-black, hsl(0deg 0% 0%));
  font-size: var(--form-input-font-size, 1rem);
  white-space: nowrap;
  text-overflow: ellipsis;
  background-color: var(--cm-color-white, hsl);
  border: var(--form-input-border-width, 1px) solid
    var(--form-input-border-color, #d9d9d9);
  border-radius: var(--form-input-border-radius, 4px);

  // remove 6px from padding-inline to align with the text input
  padding-inline: calc(var(--form-input-padding-inline, 16px))
    var(--form-input-padding-inline, 16px);
  padding-block: var(--form-input-padding-block-start, 20px)
    var(--form-input-padding-block-end, 8px);

  &--placeholder {
    color: var(--form-input-placeholder-color, #999);
  }
}
</style>
